// ---------------------------------------------------------------------------
// Water scene — sunlight filtering down from just below the surface. The
// landing hero and the login page's backdrop.
//
// The scene itself is pure CSS (layered glows and light shafts in
// styles.css, animated with transform/opacity only). This module just pauses
// those animations while nobody can see them: when a game takes over the
// page, and when the scene is scrolled out of view.
// ---------------------------------------------------------------------------

export interface SceneHooks {
  pause: () => void;
  resume: () => void;
}

const PAUSED = "is-paused";

export function initWater(scene: HTMLElement): SceneHooks {
  let pausedByGame = false;
  let offscreen = false;

  const sync = (): void => {
    scene.classList.toggle(PAUSED, pausedByGame || offscreen);
  };

  if ("IntersectionObserver" in window) {
    new IntersectionObserver((entries) => {
      for (const entry of entries) offscreen = !entry.isIntersecting;
      sync();
    }).observe(scene);
  }

  return {
    pause: () => {
      pausedByGame = true;
      sync();
    },
    resume: () => {
      pausedByGame = false;
      sync();
    },
  };
}

// The nav carries a small wordmark, hidden while the hero's large one is on
// screen so the brand never shows twice at once. The markup starts it hidden
// (the hero is what loads in view), so nothing flashes before this runs.
export function syncNavWordmark(heroMark: HTMLElement, nav: HTMLElement): void {
  const HIDDEN = "nav--brand-hidden";
  if (!("IntersectionObserver" in window)) {
    nav.classList.remove(HIDDEN);
    return;
  }
  new IntersectionObserver(
    (entries) => {
      for (const entry of entries) nav.classList.toggle(HIDDEN, entry.isIntersecting);
    },
    { threshold: 0.25 },
  ).observe(heroMark);
}
